import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Thermometer, Sun, Leaf, Sparkles } from 'lucide-react';
import { sanctuaryData } from '../mockData';
import { Button } from '../components/ui/button';

const Sanctuary = () => {
  const [selectedImage, setSelectedImage] = useState(0);

  return (
    <div className="min-h-screen pt-20">
      <Helmet>
        <title>The Sanctuary | Luxury Off-Grid Glamping Pods & Structures</title>
        <meta name="description" content="Architectural elegance meets zero-impact engineering. Sculptural pods with climate control, solar power, and luxury amenities. Investment from R500,000." />
        <link rel="canonical" href="https://goglamping.co.za/sanctuary" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-stone-100 to-sage-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold text-stone-900 mb-6">
              {sanctuaryData.title}
            </h1>
            <p className="text-2xl text-sage-800 font-semibold mb-6">
              {sanctuaryData.subtitle}
            </p>
            <p className="text-xl text-stone-600 max-w-4xl mx-auto leading-relaxed">
              {sanctuaryData.description}
            </p>
          </div>
        </div>
      </section>

      {/* Image Gallery */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <img
              src={sanctuaryData.images[selectedImage].url}
              alt="Luxury glamping sanctuary"
              className="w-full h-[600px] object-cover rounded-2xl shadow-2xl"
            />
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            {sanctuaryData.images.map((image, index) => (
              <button
                key={image.id}
                onClick={() => setSelectedImage(index)}
                className={`relative overflow-hidden rounded-xl transition-all duration-300 ${
                  selectedImage === index
                    ? 'ring-4 ring-sage-700 scale-105'
                    : 'hover:scale-105 opacity-70 hover:opacity-100'
                }`}
              >
                <img
                  src={image.url}
                  alt={`Sanctuary view ${index + 1}`}
                  className="w-full h-40 object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-24 bg-stone-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Engineering Silence & Comfort
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              The Analyst Frame: Technical mastery meets architectural beauty
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {sanctuaryData.specifications.map((spec) => {
              const iconMap = {
                'spec-thermal': Thermometer,
                'spec-offgrid': Sun,
                'spec-biomimicry': Leaf,
                'spec-materials': Sparkles
              };
              const Icon = iconMap[spec.id] || Sparkles;
              
              return (
                <div
                  key={spec.id}
                  className="bg-white/10 backdrop-blur-md p-8 rounded-2xl border border-white/20 hover:bg-white/15 transition-all duration-300"
                >
                  <div className="flex items-start space-x-4">
                    <div className="bg-sage-700 rounded-xl p-3">
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold mb-3">{spec.category}</h3>
                      <p className="text-gray-300 leading-relaxed">{spec.details}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="py-24 bg-gradient-to-br from-sage-50 to-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-stone-900 mb-6">
              Rugged Soul, Five-Star Comfort
            </h2>
            <p className="text-xl text-stone-600 max-w-3xl mx-auto">
              Imagine your guests arriving from the concrete noise of the city
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <h3 className="text-2xl font-bold text-stone-900 mb-4">The Scent</h3>
              <p className="text-stone-600 leading-relaxed">
                {sanctuaryData.experience.scent}
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <h3 className="text-2xl font-bold text-stone-900 mb-4">The Sound</h3>
              <p className="text-stone-600 leading-relaxed">
                {sanctuaryData.experience.sound}
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <h3 className="text-2xl font-bold text-stone-900 mb-4">The Comfort</h3>
              <p className="text-stone-600 leading-relaxed">
                {sanctuaryData.experience.comfort}
              </p>
            </div>
          </div>

          <div className="bg-sage-700 text-white p-12 rounded-2xl shadow-2xl text-center">
            <h3 className="text-3xl md:text-4xl font-bold mb-4">
              This is Not "Camping"
            </h3>
            <p className="text-xl text-sage-100 mb-8">
              This is high-yield hospitality that respects your privacy and honors your heritage
            </p>
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6">
              <div className="text-center">
                <div className="text-4xl font-bold mb-1">from R500 000</div>
                <div className="text-sage-200 text-sm">Investment Value</div>
              </div>
              <div className="hidden sm:block w-px h-12 bg-sage-400" />
              <div className="text-center">
                <div className="text-4xl font-bold mb-1">R5,000</div>
                <div className="text-sage-200 text-sm">Per Night Rate</div>
              </div>
              <div className="hidden sm:block w-px h-12 bg-sage-400" />
              <div className="text-center">
                <div className="text-4xl font-bold mb-1">600 TC</div>
                <div className="text-sage-200 text-sm">Thread Count Luxury</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Biomimicry Design */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-stone-900 mb-6">
                Biomimicry at Its Finest
              </h2>
              <p className="text-xl text-stone-600 mb-6 leading-relaxed">
                The soft, parabolic silhouettes blend into the landscape rather than competing with it. Each pod is designed to mimic natural outcroppings or river stones, creating an organic harmony with your land.
              </p>
              <p className="text-lg text-stone-600 mb-8 leading-relaxed">
                The aerodynamic curves aren't beautiful—they're functional. Wind flows naturally around the structure, reducing stress on materials and creating a whisper-quiet interior even in stormy conditions.
              </p>
              <Link to="/strategy">
                <Button size="lg" className="bg-sage-700 hover:bg-sage-800 text-white px-6 py-5 text-base rounded-lg shadow-lg hover:scale-105 transition-all duration-300">
                  Explore Our Strategy
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <img
                src={sanctuaryData.images[1]}
                alt="Interior luxury"
                className="w-full h-[500px] object-cover rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-xl border-4 border-sage-700">
                <div className="text-3xl font-bold text-sage-700 mb-1">100%</div>
                <div className="text-sm text-stone-600">Removable Design</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Zero Impact */}
      <section className="py-24 bg-stone-900 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Zero Permanent Scars
          </h2>
          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            No impact adjustable leg foundations mean your land returns to nature in 30 days if structures are moved. This isn't architecture—it's a commitment to your land's future.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
              <div className="text-3xl font-bold text-sage-400 mb-2">21-23°C</div>
              <div className="text-sm text-gray-300">Year-Round Climate Control</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
              <div className="text-3xl font-bold text-sage-400 mb-2">100%</div>
              <div className="text-sm text-gray-300">Off-Grid Capable</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
              <div className="text-3xl font-bold text-sage-400 mb-2">0dB</div>
              <div className="text-sm text-gray-300">External Noise Dampening</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-sage-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            See What's Possible on Your Land
          </h2>
          <p className="text-xl mb-8 text-sage-100">
            Schedule a site discovery walk and we'll identify your highest-yield impact zones
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-sage-700 hover:bg-gray-100 px-8 py-6 text-lg rounded-lg shadow-2xl hover:scale-105 transition-all duration-300">
              Request Site Discovery Walk
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Sanctuary;
